import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Download, FileSpreadsheet, RotateCcw } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { useGenerateOwnersList } from "../hooks/useOrders";
import type { ManagedFileBrief, OrderType } from "../types";

const WPW_TYPES: OrderType[] = ["wpw_terenowe", "wpw_kompleksowe"];

const buttonClass =
  "inline-flex items-center gap-1.5 rounded border border-[var(--color-border)] px-2 py-1 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-60 disabled:cursor-not-allowed";

/**
 * Wykaz właścicieli (XLSX) przekazanych do pozyskania zgód w tym zamówieniu.
 * Generowany w tle po utworzeniu zamówienia WPW i przebudowywany po zatwierdzeniu
 * zmiany zakresu; do tego ręczne „Wygeneruj" (zamówienia sprzed automatu) i
 * „Wygeneruj ponownie". Kompaktowy wiersz osadzany w sekcji „Przekazane WPW
 * (per właściciel)" (slot `ownersList` w WpwHandoverPanel).
 *
 * Nazwa pól API (`mml_*`) pochodzi od firmy, dla której wykaz powstał pierwotnie —
 * w interfejsie nie używamy jej nigdzie, bo to cudza nazwa własna, a wykaz jeździ
 * do różnych podwykonawców.
 */
export function OrderOwnersListSection({
  orderId,
  orderType,
  file,
  generatedAt,
  readOnly = false,
}: {
  orderId: string;
  orderType: OrderType;
  file: ManagedFileBrief | null;
  generatedAt: string | null;
  /** Podgląd „widok podwykonawcy" — pobieranie działa, generowanie zablokowane. */
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const generateMutation = useGenerateOwnersList(orderId);
  // Plik powstaje w tle — po zleceniu odpytujemy zamówienie, aż pojawi się plik
  // (pierwsze generowanie) albo zmieni data wygenerowania (regeneracja).
  const [waiting, setWaiting] = useState(false);
  const [baseline, setBaseline] = useState<string | null>(null);

  useEffect(() => {
    if (waiting && generatedAt !== baseline) setWaiting(false);
  }, [waiting, generatedAt, baseline]);

  useEffect(() => {
    if (!waiting) return;
    const interval = setInterval(
      () => qc.invalidateQueries({ queryKey: ["orders", orderId] }),
      3000,
    );
    return () => clearInterval(interval);
  }, [waiting, orderId, qc]);

  if (!WPW_TYPES.includes(orderType) && !file) return null;

  const handleGenerate = async () => {
    try {
      await generateMutation.mutateAsync();
      setBaseline(generatedAt);
      setWaiting(true);
    } catch {
      /* błąd pokazujemy niżej z generateMutation.isError */
    }
  };

  const generateButton = (label: string, icon: ReactNode) => (
    <button
      type="button"
      onClick={handleGenerate}
      disabled={generateMutation.isPending || readOnly}
      className={buttonClass}
    >
      {generateMutation.isPending ? <Spinner size="sm" className="w-3.5 h-3.5" /> : icon}
      {label}
    </button>
  );

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <span className="text-xs font-medium text-[var(--color-text-muted)]">Wykaz właścicieli:</span>
      {file?.url ? (
        <>
          <a
            href={file.url}
            className="inline-flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
          >
            <FileSpreadsheet size={14} className="shrink-0" />
            {file.original_filename}
            <Download size={12} className="shrink-0" />
          </a>
          {generatedAt && (
            <span className="text-xs text-[var(--color-text-muted)]">
              wygenerowano {new Date(generatedAt).toLocaleString("pl")}
            </span>
          )}
          {waiting ? (
            <span className="text-xs text-[var(--color-text-muted)]">generowanie w toku…</span>
          ) : (
            generateButton("Wygeneruj ponownie", <RotateCcw size={13} className="shrink-0" />)
          )}
        </>
      ) : waiting ? (
        <span className="text-xs text-[var(--color-text-muted)]">
          Wykaz właścicieli jest generowany w tle…
        </span>
      ) : (
        generateButton("Wygeneruj wykaz właścicieli", <FileSpreadsheet size={13} className="shrink-0" />)
      )}
      {generateMutation.isError && (
        <span className="text-xs text-red-600 dark:text-red-400">
          Nie udało się uruchomić generowania.
        </span>
      )}
    </div>
  );
}
